import { api } from 'api';
import config from 'config';
import { addressesApi } from 'services/addressesApi';
import { paymentMethodsApi } from 'services/paymentMethodsApi';
import { productsApi } from 'services/productsApi';
import { shoppingCartApi } from 'services/shoppingCartApi';
import { userProfileApi } from 'services/userProfileApi';

import { AppThunk } from 'store';
import {
  resetForm as resetBookingForm,
  setIsAuthRedirection,
  setStep
} from 'store/reducers/bookingAppointmentSlice';
import { clearShopFilters } from 'store/reducers/cartSlice';
import {
  resetForm as resetGiftCardForm,
  resetFormTo3ndStep
} from 'store/reducers/giftCardsShopSlice';
import { guestSessionFinished } from 'store/reducers/guestSlice';
import { resetForm as resetMembershipForm } from 'store/reducers/membershipsSlice';
import { resetForm } from 'store/reducers/packagesShopSlice';
import { setUserInfo } from 'store/reducers/persistAuthSlice';
import { resetServicesState } from 'store/reducers/servicesSlice';
import {
  authSessionFinished,
  clearRefCodeData,
  goToStep,
  setCommunicationType,
  setError,
  setFieldError,
  setIsAuthorized,
  setIsLoading,
  setMultipleRecords,
  setRedirectFlow,
  setRefCodeData,
  setShowWelcomePage,
  setUser,
  setUserCampaigns,
  setUserData,
  setUserPronouns
} from 'store/reducers/userSlice';

import {
  AccessCodeParams,
  BookingSteps,
  CheckAccessCodeParams,
  CheckEmailParams,
  CheckMembershipType,
  FormType,
  GenderFormType,
  LoginTypes,
  MemberData,
  PasswordType,
  RefCodeResponse
} from 'types';

import { getClientDetails } from './user';

const {
  login: { errors }
} = config;

export const login =
  (loginData: LoginTypes): AppThunk =>
  async (dispatch, getState) => {
    const guestInfo = getState().persistGuestAuth?.guestInfo;
    const {
      bookingAppointment: { isAuthRedirection }
    } = getState();

    try {
      dispatch(setIsLoading(true));
      if (guestInfo) {
        dispatch(guestSessionFinished());
        dispatch(resetFormTo3ndStep());
      }

      const loginResponse = await api.auth.login(loginData);
      if (
        Boolean(loginResponse.data.clid) &&
        Boolean(loginResponse.data.token)
      ) {
        const { data } = await api.client.getOnLoginClDetails({
          clid: loginResponse.data.clid,
          token: loginResponse.data.token
        });

        dispatch(setUser(data[0]));
        dispatch(setIsAuthorized(true));
        dispatch(setUserInfo(loginResponse.data));
        if (isAuthRedirection) {
          dispatch(setStep(BookingSteps.REVIEW));
        }
        dispatch(setIsAuthRedirection(false));
      }
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(setError(errors.wrongCredential));
      } else {
        dispatch(setError(error.response?.data?.error || error.message));
      }
      console.error(`Error while login: ${error.message}`);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const logout = (): AppThunk => async (dispatch) => {
  try {
    dispatch(setIsLoading(true));
    dispatch(authSessionFinished());
    dispatch(setUserInfo(null));
    dispatch(resetForm());
    dispatch(setStep(BookingSteps.SERVICE));
    dispatch(resetBookingForm());
    dispatch(resetGiftCardForm());
    dispatch(resetServicesState());
    dispatch(setRedirectFlow(null));
    dispatch(resetMembershipForm());
    dispatch(clearShopFilters());
    dispatch(addressesApi.util.resetApiState());
    dispatch(shoppingCartApi.util.resetApiState());
    dispatch(userProfileApi.util.resetApiState());
    dispatch(paymentMethodsApi.util.resetApiState());
    dispatch(productsApi.util.resetApiState());
  } catch (error) {
    console.error(`Error while logout: ${error.message}`);
    dispatch(setError(error.response?.data?.error || error.message));
  } finally {
    dispatch(setIsLoading(false));
  }
};

export const getUser = (): AppThunk => async (dispatch) => {
  try {
    dispatch(setIsLoading(true));
    dispatch(getClientDetails());
  } catch (error) {
    console.error(`Error while get user info: ${error.message}`);
    dispatch(authSessionFinished());
    dispatch(setUserInfo(null));
  } finally {
    dispatch(setIsLoading(false));
  }
};

export const createAccount =
  (
    formData: FormType & PasswordType & GenderFormType & RefCodeResponse
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setIsLoading(true));
      const guestInfo = getState().persistGuestAuth?.guestInfo;
      if (guestInfo) {
        dispatch(guestSessionFinished());
        dispatch(resetFormTo3ndStep());
      }
      await api.auth.createAccount(formData);

      const loginResponse = await api.auth.login({
        email: formData.email,
        pass: formData.pass,
        keep_signed: 0
      });

      dispatch(setUserInfo(loginResponse.data));
      dispatch(getClientDetails());
      dispatch(setShowWelcomePage(true));
    } catch (error) {
      dispatch(setError(error.response?.data?.error || error.message));
      console.error(`Error while create account: ${error.message}`);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const checkEmail =
  (checkEmailParams: CheckEmailParams): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const { data } = await api.auth.checkEmail({
        email: checkEmailParams.email
      });
      const responseError = data?.error;
      const relatedClientId = data?.clid;
      const isEmailAlreadyExist = relatedClientId;

      if (isEmailAlreadyExist) {
        const params = {
          email: checkEmailParams.email,
          bday: checkEmailParams?.bday as string
        };
        try {
          const { data: member } = await api.auth.checkMember(params);
          if (member) {
            const isPhoneAvailable =
              Boolean(member?.ph_mobile) && member?.sms_sent;
            const isEmailAvailable = Boolean(member?.email);

            dispatch(setUserData(member));

            if (isPhoneAvailable && isEmailAvailable) {
              dispatch(goToStep(1));
            } else if (isPhoneAvailable || isEmailAvailable) {
              dispatch(
                setCommunicationType(isEmailAvailable ? 'email' : 'sms')
              );
              dispatch(goToStep(2));
            } else {
              dispatch(goToStep(1));
            }

            checkEmailParams?.onResetFlow!();
            return false;
          }
        } catch (_) {
          return true;
        }
      } else {
        dispatch(setError(responseError));
      }
      return true;
    } catch (error) {
      const isMultipleRecords =
        error?.response?.data?.error === 'Multiple Client Records';

      if (isMultipleRecords) {
        dispatch(setUserData(error?.response?.data));
        dispatch(goToStep(1));
        dispatch(setMultipleRecords(true));
        checkEmailParams?.onResetFlow!();
        return false;
      }

      dispatch(
        setFieldError({
          field: 'email',
          error: errors.accountAlreadyExist
        })
      );
      console.error(`Error while check email: ${error.message}`);
      return false;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const checkMember =
  (
    checkMemberParams: CheckMembershipType
  ): AppThunk<Promise<MemberData | null>> =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const loginResponse = await api.auth.checkMember(checkMemberParams);
      return loginResponse.data;
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(setError(errors.noMatchMembership));
      } else {
        dispatch(setError(error.response?.data?.error || error.message));
      }
      console.error(`Error while check membership: ${error.message}`);
      return null;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const sendAccessCode =
  (accessCodeParams: AccessCodeParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const response = await api.auth.sendAccessCode(accessCodeParams);
      return response?.data;
    } catch (error) {
      dispatch(setError(error.response?.data?.error || error.message));
      console.error(`Error while send access code: ${error.message}`);
      return null;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const checkAccessCode =
  (accessCodeParams: CheckAccessCodeParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const response = await api.auth.checkAccessCode(accessCodeParams);
      return response?.data?.token;
    } catch (error) {
      dispatch(setError(error.response?.data?.error || error.message));
      console.error(`Error while confirm access code: ${error.message}`);
      return null;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const checkRefCode =
  (ref_code: string): AppThunk<Promise<RefCodeResponse>> =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const response = await api.auth.checkRefCode(ref_code);

      dispatch(setRefCodeData({ ...response?.data, ref_code }));

      return response?.data;
    } catch (error) {
      dispatch(setError(error.response?.data?.error || error.message));
      console.error(
        `Error while get reference code validity: ${error.message}`
      );
      return null;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const getRegFormFields = (): AppThunk => async (dispatch) => {
  try {
    dispatch(setIsLoading(true));
    const response = await api.auth.getRegFormFields();

    if (response?.data?.campaigns) {
      const campaigns = Array.isArray(response?.data?.campaigns)
        ? response.data.campaigns
        : [];
      dispatch(setUserCampaigns(campaigns));
    }

    if (response?.data?.pronouns) {
      const pronouns = Array.isArray(response?.data?.pronouns)
        ? response.data.pronouns
        : [];
      dispatch(setUserPronouns(pronouns));
    }
  } catch (error) {
    console.error(
      `Error while get registration form fields : ${error.message}`
    );
    return null;
  } finally {
    dispatch(setIsLoading(false));
  }
};

export const updateRefCode =
  (ref_code: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));

      const data: RefCodeResponse = await dispatch(checkRefCode(ref_code));

      if (data.is_valid) {
        const { data: response } = await api.auth.updateRefCode(data);

        if (response?.success) {
          dispatch(clearRefCodeData());
        }
      }
    } catch (error) {
      dispatch(setError(error.response?.data?.error || error.message));
      console.error(
        `Error while get reference code validity: ${error.message}`
      );
      return null;
    } finally {
      dispatch(setIsLoading(false));
    }
  };
